import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, Loader2, CheckCircle, Clock, MessageSquare, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const STATUS_MAP = {
  new: { label: 'Új', color: 'bg-blue-50 text-blue-700', icon: Clock },
  in_progress: { label: 'Feldolgozás alatt', color: 'bg-[#C5A059]/10 text-[#C5A059]', icon: Loader2 },
  processed: { label: 'Feldolgozva', color: 'bg-green-50 text-green-700', icon: CheckCircle },
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function ClientDocuments({ companyId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = useCallback(() => {
    if (!companyId) return;
    api.get(`/portal/company/${companyId}/documents`)
      .then(r => { setDocs(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [companyId]);

  React.useEffect(() => { loadDocs(); }, [loadDocs]);

  const uploadFile = async (file) => {
    setError('');
    const allowed = ['application/pdf', 'image/jpeg', 'image/png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/msword'];
    if (!allowed.includes(file.type)) {
      setError('Nem támogatott fájlformátum. Engedélyezett: PDF, JPG, PNG, XLSX, DOCX');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('A fájl mérete nem haladhatja meg a 20 MB-ot.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/portal/company/${companyId}/documents/upload`, formData);
      loadDocs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Feltöltés sikertelen.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [companyId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDownload = async (docId, filename) => {
    try {
      const resp = await api.get(`/portal/documents/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (!companyId) return null;

  return (
    <div data-testid="client-documents" className="space-y-6">
      {/* Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`rounded-lg border-2 border-dashed transition-all duration-200 ${
          dragOver ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
        }`}
      >
        <label className="flex flex-col items-center justify-center py-8 px-4 cursor-pointer">
          {uploading ? (
            <Loader2 size={28} className="text-[#C5A059] animate-spin mb-3" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#0B1B3D]/5 flex items-center justify-center mb-3">
              <Upload size={20} className="text-[#64748B]" />
            </div>
          )}
          <span className="text-sm font-medium text-[#1A1F2C]">
            {uploading ? 'Feltöltés folyamatban...' : 'Dokumentum feltöltése'}
          </span>
          <span className="text-xs text-[#64748B] mt-1">
            Húzza ide a fájlt, vagy kattintson a tallózáshoz
          </span>
          <span className="text-xs text-[#94A3B8] mt-1">PDF, JPG, PNG, XLSX, DOCX (max. 20 MB)</span>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx,.xls,.doc"
            onChange={handleFileChange} className="hidden" disabled={uploading} />
        </label>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Document List */}
      {docs.length === 0 && loaded ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center">
          <FileText size={36} className="text-[#E2E8F0] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Még nincsenek feltöltött dokumentumok.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => {
            const st = STATUS_MAP[doc.status] || STATUS_MAP.new;
            const StIcon = st.icon;
            return (
              <div key={doc.id} data-testid={`doc-${doc.id}`}
                className={`bg-white border rounded-lg p-4 ${doc.client_notified ? 'border-[#C5A059]' : 'border-[#E2E8F0]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <FileText size={18} className="text-[#64748B] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0B1B3D] truncate">{doc.original_filename}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-[#64748B]">
                          {new Date(doc.created_at).toLocaleDateString('hu-HU')}
                        </span>
                        <span className="text-xs text-[#64748B]">{formatFileSize(doc.size)}</span>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${st.color}`}>
                          <StIcon size={10} /> {st.label}
                        </span>
                        {doc.client_notified && (
                          <span className="text-xs bg-[#C5A059] text-white px-2 py-0.5 rounded">Frissítve</span>
                        )}
                      </div>
                      {doc.admin_comment && (
                        <div className="mt-2 flex items-start gap-2 p-2 bg-[#FAFAFA] rounded text-xs text-[#64748B]">
                          <MessageSquare size={12} className="mt-0.5 text-[#C5A059] flex-shrink-0" />
                          <span>{doc.admin_comment}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(doc.id, doc.original_filename)}
                    className="text-[#64748B] hover:text-[#0B1B3D] flex-shrink-0" title="Letöltés">
                    <Download size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
